import { useEffect, useState } from "react";
import { Crown, MapPin } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";

export default function CountdownBanner({ settings }) {
  const target = settings.event_date ? new Date(settings.event_date).getTime() : null;
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!target) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);

  if (!target || isNaN(target) || target <= now) return null;

  const diff = target - now;
  const units = [
    ["Days", Math.floor(diff / 86400000)],
    ["Hours", Math.floor((diff / 3600000) % 24)],
    ["Minutes", Math.floor((diff / 60000) % 60)],
    ["Seconds", Math.floor((diff / 1000) % 60)],
  ];

  return (
    <section data-testid="countdown-banner" className="border-b border-[#D4AF37]/25 bg-gradient-to-b from-[#0C0C0C] to-[#050505]">
      <div className="mx-auto max-w-7xl px-6 py-20 text-center lg:px-10">
        <Reveal>
          <span className="font-script text-3xl text-[#D4AF37] md:text-4xl">The Next Coronation Awaits</span>
          <h2 data-testid="countdown-event-name" className="font-display mt-4 text-4xl font-medium text-white md:text-5xl">
            {settings.event_name || "The Grand Pageant"}
          </h2>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.3em] text-neutral-400">
            {settings.event_venue && (
              <span data-testid="countdown-event-venue" className="flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" strokeWidth={1.5} /> {settings.event_venue}
              </span>
            )}
            <span data-testid="countdown-event-date">
              {new Date(target).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </span>
          </div>
          <div className="mt-12 flex items-start justify-center gap-5 md:gap-10">
            {units.map(([label, v], i) => (
              <div key={label} className="flex items-start gap-5 md:gap-10">
                {i > 0 && <span className="mt-2 h-14 w-px bg-[#D4AF37]/30 md:h-20" />}
                <div>
                  <div data-testid={`countdown-${label.toLowerCase()}`} className="font-display gold-gradient-text text-5xl font-medium tabular-nums md:text-7xl">
                    {String(v).padStart(2, "0")}
                  </div>
                  <div className="mt-2 text-[9px] uppercase tracking-[0.35em] text-neutral-400 md:text-[10px]">{label}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href="/register"
              data-testid="countdown-register-button"
              className="inline-flex items-center gap-3 bg-[#D4AF37] px-10 py-4 text-xs font-medium uppercase tracking-[0.3em] text-black transition-colors duration-300 hover:bg-[#F9F1CC]"
            >
              <Crown className="h-4 w-4" strokeWidth={1.5} /> Register Before the Crown Passes
            </a>
            <a
              href="/events/mrs-south-india-2026"
              data-testid="countdown-event-details-link"
              className="inline-flex items-center gap-3 border border-[#D4AF37]/50 px-10 py-4 text-xs uppercase tracking-[0.3em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#D4AF37] hover:text-black"
            >
              View Event Details
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
